import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, MapPin, Loader2, Archive, Layers, Building2 } from "lucide-react";
import { venuesApi, VENUE_TYPES } from "../../lib/events";
import { toast } from "sonner";

function VenueTypeLabel({ value }) {
  const m = VENUE_TYPES.find((t) => t.value === value);
  return <>{m ? m.label : "—"}</>;
}

export default function Venues() {
  const [venues, setVenues] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = () => {
    setVenues(null);
    venuesApi.list(showArchived)
      .then(setVenues)
      .catch((e) => setError(e?.response?.data?.detail || "Could not load venues."));
  };
  useEffect(load, [showArchived]);

  const handleArchive = async (id) => {
    if (!window.confirm("Archive this venue? It will be hidden from new events.")) return;
    try {
      await venuesApi.archive(id);
      toast.success("Venue archived");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive venue.");
    }
  };

  return (
    <section className="p-6 sm:p-10" data-testid="venues-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="gt-label mb-2">Venue templates</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
              Your venues
            </h2>
            <p className="text-[var(--gt-ink-2)] text-sm mt-2 max-w-lg">
              Create reusable venue layouts and seating templates. Attach them
              to events to auto-generate ticket inventory.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs inline-flex items-center gap-2 text-[var(--gt-ink-2)]">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="venues-show-archived"
              />
              Show archived
            </label>
            <button
              onClick={() => navigate("/dashboard/venues/new")}
              className="gt-btn-primary"
              data-testid="venue-create-btn"
            >
              <Plus size={16} strokeWidth={2.5} /> New venue
            </button>
          </div>
        </div>

        {venues === null && !error && (
          <div className="flex items-center gap-3 text-[var(--gt-ink-2)]" data-testid="venues-loading">
            <Loader2 size={16} className="animate-spin" /> <span className="text-sm">Loading venues…</span>
          </div>
        )}

        {error && (
          <div className="gt-card border-[var(--gt-accent)] text-[var(--gt-accent)]" data-testid="venues-error">
            {error}
          </div>
        )}

        {venues && venues.length === 0 && !error && (
          <div className="border border-[var(--gt-line)] rounded-sm p-10 text-center bg-white" data-testid="venues-empty">
            <Building2 size={32} className="mx-auto mb-4 text-[var(--gt-ink-3)]" />
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">
              You have no venues yet.
            </h3>
            <p className="text-[var(--gt-ink-2)] text-sm max-w-md mx-auto mb-6">
              Create your first venue — you'll be able to reuse the layout across
              multiple events.
            </p>
            <button onClick={() => navigate("/dashboard/venues/new")} className="gt-btn-primary" data-testid="venues-empty-create">
              <Plus size={16} /> Create venue
            </button>
          </div>
        )}

        {venues && venues.length > 0 && (
          <div className="flex flex-col gap-3" data-testid="venues-list">
            {venues.map((v) => (
              <Link
                key={v.id}
                to={`/dashboard/venues/${v.id}`}
                className="border border-[var(--gt-line)] rounded-sm hover:border-[var(--gt-ink)] bg-white p-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] items-start gap-4"
                data-testid={`venue-card-${v.id}`}
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-heading text-xl font-bold tracking-tight">{v.name}</h3>
                    {v.status === "archived" && (
                      <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-sm bg-[var(--gt-bg-surface)] text-[var(--gt-ink-3)] border border-[var(--gt-line)]" data-testid={`venue-status-archived-${v.id}`}>
                        Archived
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[var(--gt-ink-2)]">
                    <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {v.suburb || "—"}{v.state ? `, ${v.state}` : ""}</span>
                    <span><VenueTypeLabel value={v.venue_type} /></span>
                    <span>Capacity: {v.capacity}</span>
                    <span className="inline-flex items-center gap-1.5"><Layers size={12} /> {v.templates_count} template{v.templates_count === 1 ? "" : "s"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:justify-end">
                  {v.status === "active" && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); handleArchive(v.id); }}
                      className="text-xs text-[var(--gt-ink-2)] hover:text-[var(--gt-accent)] inline-flex items-center gap-1"
                      data-testid={`venue-archive-${v.id}`}
                    >
                      <Archive size={11} /> Archive
                    </button>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
